// A file as it appears in a list: a card on the files page, one line on a topic's page.

import { useState } from 'react';
import { Link } from 'react-router-dom';

import type { ApiClient } from '../api/client.js';
import type { FileView, Person } from '../api/contract.js';
import { fileProblem } from './AddFile.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { FileEditPanel, FileMenu, type FileMode } from './FileMenu.js';

const firstName = (name: string): string => name.split(' ')[0] ?? name;

export function visibilityLabel(file: FileView, people: readonly Person[]): string {
  const visibility = file.visibility;
  if (visibility.kind === 'principals') {
    return 'All principals';
  }
  if (visibility.kind === 'everyone') {
    return 'Everyone in the plan';
  }
  const person = people.find((item) => item.id === visibility.personId);
  return person === undefined || person.self
    ? 'Just you'
    : `Just ${firstName(person.name)}`;
}

/** The row's detail-line wording: the label with context, e.g. "Visible to just you". */
function visibleTo(file: FileView, people: readonly Person[]): string {
  const label = visibilityLabel(file, people);
  return `Visible to ${label.charAt(0).toLowerCase()}${label.slice(1)}`;
}

export function uploaderLabel(file: FileView, people: readonly Person[]): string {
  const person = people.find((item) => item.id === file.uploadedBy);
  if (person === undefined) {
    return 'Added';
  }
  return person.self ? 'Added by you' : `Added by ${firstName(person.name)}`;
}

/** The words for a file that is not finished, or null when it is. */
export function stateWords(file: FileView): string | null {
  return file.state === 'reading'
    ? 'Processing'
    : file.state === 'failed'
      ? 'Couldn’t be processed'
      : null;
}

type RowProps = {
  client: ApiClient;
  planId: string;
  file: FileView;
  people: readonly Person[];
  viewerIsPrincipal: boolean;
  onChanged: () => void;
};

function useRow(onChanged: () => void) {
  const [mode, setMode] = useState<FileMode>('view');
  const [problem, setProblem] = useState<string | null>(null);
  return {
    mode,
    setMode,
    problem,
    setProblem,
    done: (): void => {
      setMode('view');
      onChanged();
    },
  };
}

export function FileCard(props: RowProps): JSX.Element {
  const { client, planId, file, people, viewerIsPrincipal } = props;
  const row = useRow(props.onChanged);
  const words = stateWords(file);
  return (
    <li className="fileCard">
      {row.mode !== 'change' ? (
        <>
          <div className="fileCard__info">
            <Link className="fileCard__name" to={`/plans/${planId}/files/${file.id}`}>
              {file.name}
            </Link>
            <span className="fileCard__line">
              {uploaderLabel(file, people)} · {visibleTo(file, people)}
            </span>
            {row.problem === null ? null : (
              <span className="artifact__problem" role="alert">
                {row.problem}
              </span>
            )}
          </div>
          {words === null ? null : (
            <span
              className={`fileChip${file.state === 'failed' ? ' fileChip--failed' : ''}`}
            >
              {words}
            </span>
          )}
          <FileMenu
            client={client}
            planId={planId}
            file={file}
            onMode={row.setMode}
            onProblem={row.setProblem}
          />
        </>
      ) : (
        <FileEditPanel
          client={client}
          planId={planId}
          file={file}
          people={people}
          viewerIsPrincipal={viewerIsPrincipal}
          onCancel={() => row.setMode('view')}
          onDone={row.done}
        />
      )}
      {row.mode === 'delete' ? (
        <ConfirmDialog
          title="Delete this file?"
          name={file.name}
          detail="The contents of this file will no longer be available to Mor."
          confirmLabel="Delete"
          problemFor={fileProblem}
          onConfirm={async () => {
            await client.deleteFile(planId, file.id);
            row.done();
          }}
          onCancel={() => row.setMode('view')}
        />
      ) : null}
    </li>
  );
}

/** `card` is a box like a topic's question card: icon, name, state line, menu. */
export function FileLine(props: RowProps & { variant?: 'line' | 'card' }): JSX.Element {
  const { client, planId, file, people, viewerIsPrincipal } = props;
  const row = useRow(props.onChanged);
  const words = stateWords(file);
  const card = props.variant === 'card';
  return (
    <li className={card ? 'person fileRow' : 'fileLine'}>
      {row.mode !== 'change' ? (
        <>
          {card ? (
            <>
              <svg
                className="fileRow__icon"
                width="14"
                height="16"
                viewBox="0 0 14 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.3"
                aria-hidden="true"
              >
                <path d="M2 1h7l3 3v11H2z" />
                <path d="M9 1v3h3" />
              </svg>
              <span className="person__identity">
                <Link
                  className="person__name person__name--link"
                  to={`/plans/${planId}/files/${file.id}`}
                >
                  {file.name}
                </Link>
                <span className="person__detail">
                  {words ?? 'Read by Mor'} · {uploaderLabel(file, people)}
                </span>
                {row.problem === null ? null : (
                  <span className="artifact__problem" role="alert">
                    {row.problem}
                  </span>
                )}
              </span>
            </>
          ) : (
            <>
              <Link className="fileLine__name" to={`/plans/${planId}/files/${file.id}`}>
                {file.name}
              </Link>
              {words === null ? null : <span className="fileLine__state">{words}</span>}
              {row.problem === null ? null : (
                <span className="artifact__problem" role="alert">
                  {row.problem}
                </span>
              )}
            </>
          )}
          <FileMenu
            client={client}
            planId={planId}
            file={file}
            onMode={row.setMode}
            onProblem={row.setProblem}
          />
        </>
      ) : (
        <FileEditPanel
          client={client}
          planId={planId}
          file={file}
          people={people}
          viewerIsPrincipal={viewerIsPrincipal}
          onCancel={() => row.setMode('view')}
          onDone={row.done}
        />
      )}
      {row.mode === 'delete' ? (
        <ConfirmDialog
          title="Delete this file?"
          name={file.name}
          detail="The contents of this file will no longer be available to Mor."
          confirmLabel="Delete"
          problemFor={fileProblem}
          onConfirm={async () => {
            await client.deleteFile(planId, file.id);
            row.done();
          }}
          onCancel={() => row.setMode('view')}
        />
      ) : null}
    </li>
  );
}
